import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { ShieldCheck, ShieldX, ExternalLink } from 'lucide-react';

import { EmptyState } from '@/components/ui/empty-state';
import { CopyButton } from '@/components/admin/copy-button';
import { FilterChips } from '@/components/admin/filter-chips';
import { StatusBadge } from '@/components/ui/status-badge';
import { organizationStatusTone } from '@/lib/status';
import { useOrganizationKindLabel, useOrgModerationStatusLabel } from '@/lib/labels';
import { useCityLabel } from '@/i18n/use-city-label';
import { useTranslations } from '@/i18n/use-locale';
import { formatCount } from '@/lib/format';
import { isoDate, type ListReturnState } from '@/lib/detail';
import { useDecideModeration } from './use-decide-moderation';
import { QueryGate } from '@/components/ui/query-gate';
import { useModerationQueueQuery } from '@/lib/queries';
import { ReasonConfirm, type ConfirmMode } from './reason-confirm';
import { fillTemplate } from '@/i18n/fill-template';
import { Card } from '@/components/ui/card';
import { PageHeader } from '@/components/admin/page-header';
import { parseStatus, type StatusFilter } from './verification-status-filter';

export function VerificationRoute() {
  const t = useTranslations('admin.verification');
  const common = useTranslations('common');
  const cityLabel = useCityLabel();
  const kindLabel = useOrganizationKindLabel();
  const statusLabel = useOrgModerationStatusLabel();
  // Which row has a confirmation open, and which one. Approval asks first too (#88): it
  // is granted for good.
  const [confirm, setConfirm] = useState<{ id: string; mode: ConfirmMode } | null>(null);
  const toggleConfirm = (id: string, mode: ConfirmMode) =>
    setConfirm((current) => (current?.id === id && current.mode === mode ? null : { id, mode }));
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = parseStatus(searchParams.get('status'));
  const returnState: ListReturnState = { from: `${location.pathname}${location.search}` };
  const setStatusFilter = (next: StatusFilter) =>
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        if (next === 'all') params.delete('status');
        else params.set('status', next);
        return params;
      },
      { replace: true },
    );

  const queue = useModerationQueueQuery('organization');

  // The toast, the queue and the shell's badges are the hook's; closing this list's own
  // confirmation is all that is left to do here, and only once the API said yes.
  const decideMutation = useDecideModeration('organization', () => setConfirm(null));

  const items = useMemo(() => queue.data?.items ?? [], [queue.data]);

  const counts = useMemo(() => {
    const by = (status: string) =>
      items.filter((item) => item.organization?.status === status).length;
    return {
      all: items.length,
      pending: by('pending'),
      verified: by('verified'),
      rejected: by('rejected'),
    };
  }, [items]);

  const filtered =
    statusFilter === 'all'
      ? items
      : items.filter((item) => item.organization?.status === statusFilter);

  return (
    <QueryGate query={queue}>
      {() => {
        return (
          <div className="space-y-6">
            <PageHeader title={t('heading')} sub={t('subheading')} />

            {items.length > 0 && (
              <FilterChips<StatusFilter>
                label={t('filter.label')}
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: 'all', label: t('filter.all'), count: counts.all },
                  { value: 'pending', label: statusLabel('pending'), count: counts.pending },
                  { value: 'verified', label: statusLabel('verified'), count: counts.verified },
                  { value: 'rejected', label: statusLabel('rejected'), count: counts.rejected },
                ]}
                trailing={fillTemplate(
                  t('resultCount'),
                  'count',
                  <span className="num font-bold text-navy">{formatCount(filtered.length)}</span>,
                )}
              />
            )}

            {filtered.length === 0 ? (
              <EmptyState icon={ShieldCheck} title={t('empty.title')} hint={t('emptyHint')} />
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {filtered.map((item) => {
                  const org = item.organization;
                  if (!org) return null;
                  const isPending = org.status === 'pending';
                  return (
                    // The card opens the full request, by pointer or by Enter/Space on the
                    // focused card; its own controls (copy, the letter link, approve and
                    // reject) keep their clicks.
                    <Card
                      key={item.id}
                      data-testid="verification-card"
                      aria-label={org.name}
                      onActivate={() =>
                        void navigate(`/verification/${item.id}`, { state: returnState })
                      }
                      className="flex flex-col justify-between p-5 shadow-sm"
                    >
                      <div>
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <h3 className="text-[16px] font-bold text-navy">
                              <Link
                                to={`/verification/${item.id}`}
                                state={returnState}
                                className="rounded transition hover:text-pitch hover:underline focus-visible:ring-2 focus-visible:ring-pitch focus-visible:outline-none"
                              >
                                {org.name}
                              </Link>
                            </h3>
                            <p className="mt-0.5 text-[12px] text-conc-500">
                              {kindLabel(org.kind)}
                              {cityLabel(org.city) && ` · ${cityLabel(org.city)}`} ·{' '}
                              {fillTemplate(
                                t('submitted'),
                                'date',
                                <span className="num">{isoDate(item.submittedAt)}</span>,
                              )}
                            </p>
                          </div>
                          <StatusBadge tone={organizationStatusTone(org.status)}>
                            {statusLabel(org.status)}
                          </StatusBadge>
                        </div>

                        <dl className="mt-4 grid grid-cols-2 gap-3 rounded-xl bg-sideline p-3.5 text-[12.5px]">
                          <div>
                            <dt className="text-conc-500">{t('field.requester')}</dt>
                            <dd className="font-semibold text-navy">{org.requestedBy.fullName}</dd>
                          </div>
                          {/* An owner account signs in with the official email and may have no
                        phone at all; the requester's own email, or a dash, stands in. */}
                          {org.requestedBy.phone ? (
                            <div>
                              <dt className="text-conc-500">{t('field.phone')}</dt>
                              <dd className="num ltr-field font-semibold text-navy">
                                {org.requestedBy.phone}
                              </dd>
                            </div>
                          ) : (
                            <div>
                              <dt className="text-conc-500">{t('field.requesterEmail')}</dt>
                              <dd className="ltr-field truncate font-semibold text-navy">
                                {org.requestedBy.email || '—'}
                              </dd>
                            </div>
                          )}
                          {org.officialEmail && (
                            <div className="col-span-2">
                              <dt className="text-conc-500">{t('field.officialEmail')}</dt>
                              <dd className="flex items-center gap-1">
                                <span className="ltr-field truncate font-semibold text-navy">
                                  {org.officialEmail}
                                </span>
                                <CopyButton
                                  value={org.officialEmail}
                                  label={common('copyEmail')}
                                  copiedLabel={common('copied')}
                                />
                              </dd>
                            </div>
                          )}
                          {org.commercialRegistration && (
                            <div>
                              <dt className="text-conc-500">{t('field.cr')}</dt>
                              <dd className="num font-semibold text-navy">
                                {org.commercialRegistration}
                              </dd>
                            </div>
                          )}
                        </dl>

                        {item.previewUrl && (
                          <a
                            href={item.previewUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-3 inline-flex items-center gap-2 text-[13px] font-semibold text-pitch-700 hover:underline"
                          >
                            <ExternalLink className="h-4 w-4" />
                            {t('ceoLetter')}
                          </a>
                        )}
                      </div>

                      {isPending && (
                        <div data-keep-click>
                          <div className="mt-4 flex gap-2 border-t border-conc-100 pt-4">
                            <Button
                              onClick={() => toggleConfirm(item.id, 'approve')}
                              className="flex-1 gap-1.5"
                            >
                              <ShieldCheck aria-hidden className="h-4 w-4" />
                              {t('approve')}
                            </Button>
                            <Button
                              variant="danger-soft"
                              onClick={() => toggleConfirm(item.id, 'reject')}
                              className="gap-1.5 px-3"
                            >
                              <ShieldX aria-hidden className="h-4 w-4" />
                              {t('reject.action')}
                            </Button>
                          </div>

                          {confirm?.id === item.id && (
                            <ReasonConfirm
                              key={confirm.mode}
                              subject="organization"
                              mode={confirm.mode}
                              name={org.name}
                              pending={decideMutation.isPending}
                              onApprove={() =>
                                decideMutation.mutate({ itemId: item.id, decision: 'approve' })
                              }
                              onReject={(reason) =>
                                decideMutation.mutate({
                                  itemId: item.id,
                                  decision: 'reject',
                                  reason,
                                })
                              }
                              onCancel={() => setConfirm(null)}
                              className="mt-2"
                            />
                          )}
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        );
      }}
    </QueryGate>
  );
}
